import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Save, RotateCcw, Send, ArrowLeft, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/RichTextEditor";

const TABS = [
  { id: "audition_signup", label: "Signup confirmation", hint: "Sent automatically to every auditioner right after they submit the public sign-up form." },
  { id: "casting_cast", label: "Cast offer", hint: "Sent when you finalize auditions to each person you cast." },
  { id: "casting_reject", label: "Not cast", hint: "Sent when you finalize auditions to each auditioner who wasn't selected." },
];

/**
 * Admin email-template editor. One page, one tab per email type. Variables
 * like {{first_name}} can be dropped into subject or body. "Reset" reverts
 * to the built-in default. All three templates live in one place.
 */
export default function EmailTemplates() {
  const { user, production } = useAuth();
  // production_staff runs audition cycles day-to-day and needs to be
  // able to fine-tune the cast / do-not-cast copy without waiting on
  // the org_admin. Keeping the same enable-flag gate everywhere makes
  // subject, body, variable chips, Save, and Reset behave as one unit.
  const isAdmin = user?.role === "org_admin" || user?.role === "production_staff" || user?.role === "super_admin";
  const [tab, setTab] = useState("audition_signup");
  const [state, setState] = useState({ templates: [], variables: [], defaults: {} });
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  // Real data for the preview — a recent auditioner and a sample character
  // from THIS production, so directors see the copy the way it will actually
  // land in their inboxes, not a fixture named "Emma Rossi".
  const [sampleAud, setSampleAud] = useState(null);
  const [sampleChar, setSampleChar] = useState(null);

  const load = async () => {
    try {
      const [tpls, auds, chars] = await Promise.all([
        api.get("/email-templates"),
        api.get("/auditions").catch(() => ({ data: [] })),
        api.get("/characters").catch(() => ({ data: [] })),
      ]);
      setState(tpls.data);
      const d = {};
      tpls.data.templates.forEach((t) => { d[t.kind] = { subject: t.subject, body_html: t.body_html, is_custom: t.is_custom }; });
      setDrafts(d);
      // Prefer someone already marked "cast" so the character preview is
      // realistic; otherwise pick the most recent auditioner.
      const auditions = Array.isArray(auds.data) ? auds.data : [];
      const cast = auditions.find((a) => a.status === "cast");
      setSampleAud(cast || auditions[0] || null);
      const characters = Array.isArray(chars.data) ? chars.data : [];
      setSampleChar(characters[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load templates");
    }
  };
  useEffect(() => { load(); }, []);

  const active = drafts[tab] || { subject: "", body_html: "", is_custom: false };
  const activeMeta = TABS.find((t) => t.id === tab);

  const setField = (k, v) => setDrafts((d) => ({ ...d, [tab]: { ...d[tab], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${tab}`, { subject: active.subject, body_html: active.body_html });
      toast.success("Template saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/email-templates/${tab}`);
      toast.success("Reset to default");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const insertVar = (key) => {
    // Insert into body_html at end for simplicity — most edits are template-level, not caret-precise.
    setField("body_html", `${active.body_html || ""}{{${key}}}`);
  };

  const previewCtx = useMemo(() => {
    // Pull the real production, character, and (if we can find one) the
    // most recent auditioner so what the director sees in the preview is
    // exactly the copy their auditioners will get. Fall back to empty
    // brackets like "{{name}}" ONLY as a last resort so directors can
    // spot missing data before hitting Save.
    const audName = sampleAud?.name || "";
    const firstName = audName ? audName.split(" ")[0] : "";
    const charName = sampleAud?.cast_role || sampleChar?.name || "";
    return {
      first_name: firstName || "{{first_name}}",
      name: audName || "{{name}}",
      email: sampleAud?.email || "{{email}}",
      show_title: production?.title || "{{show_title}}",
      director: production?.director || "The Production Team",
      theatre: production?.theater_name || "{{theatre}}",
      character: charName || "{{character}}",
      slot_line: "Your slot is <b>Fri, Aug 10 at 6:00 PM</b>. ",
      slot_time: "Fri, Aug 10 at 6:00 PM",
      temp_password_block: "<p style='margin:0 0 8px;'>Your temporary password: <code style='background:#0A0A0A;padding:4px 8px;border-radius:4px;color:#F59E0B;'>Curtain42</code></p>",
    };
  }, [production, sampleAud, sampleChar]);
  const previewLabel = useMemo(() => {
    const who = sampleAud?.name ? sampleAud.name : "no auditioner yet";
    const show = production?.title || "this production";
    return `${who} · ${show}`;
  }, [production, sampleAud]);
  const previewSubject = renderTemplate(active.subject, previewCtx);
  const previewBody = renderTemplate(active.body_html, previewCtx);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="email-templates-page">
      <header>
        <Link to="/auditions" data-testid="et-back-to-auditions" className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-gold inline-flex items-center gap-1 mb-2"><ArrowLeft className="w-3 h-3" /> Back to auditions</Link>
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Casting</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Audition emails</h1>
        <p className="mt-1 text-zinc-400 text-sm max-w-2xl">Customise the three emails StageCall sends around auditions — signup confirmation, cast offer, and the not-cast update. Drop any variable into the subject or body using <code className="text-gold">{"{{"}variable{"}}"}</code>.</p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit" data-testid="et-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`et-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`h-9 px-3 rounded text-sm transition-colors ${tab === t.id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
            {t.label}
            {drafts[t.id]?.is_custom && <span className="ml-1.5 text-[9px] uppercase tracking-widest text-black/70">custom</span>}
          </button>
        ))}
      </div>

      <p className="text-[12px] text-zinc-500 inline-flex items-center gap-1.5"><Info className="w-3 h-3" /> {activeMeta?.hint}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="rounded-lg border border-white/10 bg-[#121212] p-5 space-y-4">
          <div>
            <Label>Subject</Label>
            <Input data-testid="et-subject-input" value={active.subject || ""} onChange={(e) => setField("subject", e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" disabled={!isAdmin} />
          </div>
          <div>
            <Label>Body</Label>
            <div className="mt-1">
              <RichTextEditor
                testid="et-body-editor"
                value={active.body_html || ""}
                onChange={(html) => setField("body_html", html)}
                disabled={!isAdmin}
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Variables — click to append to body</div>
            <div className="flex flex-wrap gap-1.5">
              {state.variables.map((v) => (
                <button key={v.key} data-testid={`et-var-${v.key}`} disabled={!isAdmin} onClick={() => insertVar(v.key)}
                  title={v.hint}
                  className="text-[10px] px-2 py-1 rounded border border-white/10 text-zinc-300 hover:border-gold/40 hover:text-gold hover:bg-gold/5 disabled:opacity-50">
                  {"{{"}{v.key}{"}}"}
                </button>
              ))}
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
              <button data-testid="et-save-btn" onClick={save} disabled={saving} style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }} className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60">
                <Save className="w-3.5 h-3.5" /> Save changes
              </button>
              {active.is_custom && (
                <button data-testid="et-reset-btn" onClick={reset} disabled={saving} className="h-9 px-3 rounded border border-white/10 text-sm inline-flex items-center gap-1.5 hover:border-white/25 text-zinc-300">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset to default
                </button>
              )}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-white/10 bg-[#0F0F0F] p-5" data-testid="et-preview">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5 mb-3"><Send className="w-3 h-3" /> Preview ({previewLabel})</div>
          <div className="rounded-md bg-[#0A0A0A] border border-white/10 p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Subject</div>
            <div className="mt-1 text-sm text-zinc-100" data-testid="et-preview-subject">{previewSubject || <span className="text-zinc-600">(empty)</span>}</div>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-500">Body</div>
            <div className="mt-1 prose prose-invert prose-sm max-w-none text-sm text-zinc-200" data-testid="et-preview-body" dangerouslySetInnerHTML={{ __html: previewBody || "<span style='color:#666'>(empty)</span>" }} />
          </div>
          <div className="mt-3 text-[10px] text-zinc-600 leading-relaxed">
            <Mail className="w-3 h-3 inline-block mr-1" />
            StageCall sends this via Resend using your configured sender address. Signup confirmations go out immediately after the auditioner submits. Cast + not-cast emails go out when an admin clicks <b>Finalize auditions</b>.
          </div>
        </section>
      </div>
    </div>
  );
}

function renderTemplate(text, ctx) {
  if (!text) return "";
  let out = text;
  for (const [k, v] of Object.entries(ctx)) {
    out = out.split(`{{${k}}}`).join(v == null ? "" : String(v));
  }
  return out;
}
